import React from 'react'

import { ConfigLoadingStatus } from '@src/enums'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import CompactTokensListItem from '@src/pages/Exchange/components/TokenSelectorModal/components/CompactTokensListItem'
import { IToken } from '@src/types/token'
import { useConfigStore } from '@src/zustand_stores'

interface CompactTokensListProps {
    tokens: IToken[]
    selectedToken?: IToken
    onTokenClicked: (token: IToken) => void
    getTokenBalance?: (token: IToken) => { tokenBalance: string; usdBalance: string } | undefined
    currentlySelectedToken?: IToken
}

export const CompactTokensList: React.FC<CompactTokensListProps> = ({
    tokens,
    selectedToken,
    onTokenClicked,
    getTokenBalance,
    currentlySelectedToken,
}) => {
    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()

    const isConfigurationLoading = React.useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )
    return (
        <div className='space-y-2'>
            {isConfigurationLoading ? (
                // Show placeholder skeletons when configuration is loading
                <>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <div
                            key={index}
                            className='flex animate-pulse items-center rounded-lg p-2 hover:bg-neutral-800'
                        >
                            {/* Token icon placeholder */}
                            <div className='mr-3 h-8 w-8 rounded-full bg-neutral-700'></div>

                            <div className='flex flex-1 flex-col'>
                                {/* Token name placeholder */}
                                <div className='mb-1 h-4 w-24 rounded bg-neutral-700'></div>
                                {/* Token symbol placeholder */}
                                <div className='h-3 w-16 rounded bg-neutral-700'></div>
                            </div>
                        </div>
                    ))}
                </>
            ) : (
                // Show actual tokens when loaded
                <>
                    {tokens.map(token => {
                        const balance = getTokenBalance?.(token)
                        const isCurrentlySelected =
                            currentlySelectedToken &&
                            token.address === currentlySelectedToken.address &&
                            token.chainId === currentlySelectedToken.chainId

                        return (
                            <CompactTokensListItem
                                key={`${token.chainId}-${token.address}`}
                                selectedToken={selectedToken}
                                token={token}
                                onTokenClicked={onTokenClicked}
                                userBalanceInTokens={balance?.tokenBalance}
                                userBalanceInUsd={balance?.usdBalance}
                                isCurrentlySelected={isCurrentlySelected}
                            />
                        )
                    })}
                </>
            )}
        </div>
    )
}

export default CompactTokensList
